// ═══════════════════════════════════════════════════════════
// Unified Position Types
//
// Single source of truth for all position types.
// Each category keeps its original API field names for backward
// compatibility. The `category` discriminator enables type-safe
// branching, and positionUtils provides cross-category accessors
// (getPeriodDate, getQuantity, etc.) so callers don't need to
// know which field name each category uses.
// ═══════════════════════════════════════════════════════════

// ─── Enums / Literal Types ─────────────────────────────────

export type PositionCategory = 'supply' | 'demand' | 'hedge';

export type SupplySubtype =
  | 'on_hand_inventory'
  | 'purchase_order'
  | 'ratio_po'
  | 'inbound_shipment'
  | 'production_plan';

export type DemandSubtype = 'hard_contract' | 'forecast';

export type HedgeInstrumentType =
  | 'future'
  | 'forward'
  | 'option'
  | 'swap'
  | 'other';

export type HedgeDirection = 'long' | 'short';
export type HedgeStatus = 'open' | 'closed';

// ─── Supply Position ────────────────────────────────────────

export interface SupplyPosition {
  category: 'supply';

  // Identity
  id: number;
  account_id: number;
  formula_id: number;
  formula_code: string;
  formula_name: string;

  // Classification
  supply_type: SupplySubtype;
  flow_direction: string; // 'INBOUND'

  // Time
  delivery_or_available_period: string; // YYYY-MM-DD

  // Quantity
  quantity: number; // Effective qty (remaining for POs, full for on-hand)
  total_quantity: number; // Original full quantity from DB
  remaining_quantity: number | null;
  supplied_quantity: number | null;
  quantity_uom: string | null;

  // Price
  unit_price_amount: number | null;
  unit_price_currency: string | null;

  // Supply-specific
  location: string | null;
  po_reference: string | null;
  po_batch_id: string | null;
  supplier_name: string | null;
  customer_name: string | null;
  is_purchase_order: boolean | null;
  exclude_from_calcs: boolean | null;
  original_due_date: string | null; // Original delivery date before overdue adjustment
  notes: string | null; // General-purpose note (e.g. FX conversion, overdue info)

  // Provenance
  upload_batch_id: number;
  created_at: string;
  updated_at: string;
}

// ─── Demand Position ────────────────────────────────────────

export interface DemandPosition {
  category: 'demand';

  // Identity
  id: number;
  account_id: number;
  formula_id: number;
  formula_code: string;
  formula_name: string;

  // Classification
  demand_kind: DemandSubtype;
  flow_direction: string; // 'OUTBOUND'

  // Time
  delivery_period: string; // YYYY-MM-DD

  // Quantity
  quantity: number;
  quantity_uom: string | null;

  // Price
  unit_price_amount: number | null;
  unit_price_currency: string | null;

  // Demand-specific
  customer_name: string | null;
  order_reference: string | null;

  // Pro-ration (set when account has prorate_current_month_demand enabled)
  original_quantity: number | null; // Raw MRP value before pro-ration
  proration_factor: number | null;  // remaining_days / total_days (e.g. 0.548)

  // Provenance
  upload_batch_id: number;
  created_at: string;
  updated_at: string;
}

// ─── Hedge Position ─────────────────────────────────────────

export interface HedgeContract {
  contract_id: number;
  broker_name: string | null;
  statement_date: string | null;
  exchange_code: string | null;
  contract_code: string | null;
  underlying_formula_id: number | null;
  underlying_formula_code: string | null;
  underlying_formula_name: string | null;
  contracts_count: number | null;
  contract_size_mt: number | null;
  notional_underlying_mt: number | null;
  underlying_price: number | null;
  contract_currency: string | null;
}

export interface HedgeAllocation {
  allocation_id: number;
  allocation_percent: number | null;
  conversion_ratio: number | null;
  allocated_underlying_mt: number | null;
  hedged_formula_mt: number | null;
  formula_price: number | null;
  time_allocation_mode: string | null;
  time_allocation_params: any | null;
}

export interface HedgePosition {
  category: 'hedge';

  // Identity — note: API returns hedge_position_id, not id
  hedge_position_id: number;
  account_id: number;
  formula_id: number;
  formula_code: string;
  formula_name: string;

  // Classification
  instrument_type: HedgeInstrumentType;
  position_side: HedgeDirection;
  status: HedgeStatus;

  // Time
  maturity_date: string; // YYYY-MM-DD

  // Quantity
  hedged_quantity_mt: number;
  hedge_effect: number | null; // Signed qty (+ for long, - for short)

  // Price / Currency
  hedge_currency: string | null;

  // Contract details (from hedge_positions_detailed view)
  contract_id: number | null;
  broker_name: string | null;
  statement_date: string | null;
  exchange_code: string | null;
  contract_code: string | null;
  underlying_formula_id: number | null;
  underlying_formula_code: string | null;
  underlying_formula_name: string | null;
  contracts_count: number | null;
  contract_size_mt: number | null;
  notional_underlying_mt: number | null;
  underlying_price: number | null;
  contract_currency: string | null;

  // Allocation details (from hedge_positions_detailed view)
  allocation_id: number | null;
  allocation_percent: number | null;
  conversion_ratio: number | null;
  allocated_underlying_mt: number | null;
  hedged_formula_mt: number | null;
  formula_price: number | null;
  time_allocation_mode: string | null;
  time_allocation_params: any | null;

  // Option-specific fields (only set when instrument_type = 'option')
  option_type: 'call' | 'put' | null;
  strike_price: number | null;
  premium_paid: number | null;
  premium_current: number | null;

  // Provenance
  upload_batch_id: number;
  created_at: string;
  updated_at: string;
}

// ─── Derived Hedge Position ─────────────────────────────────
// Created by the hedge derivation service when base hedges are
// allocated to derived materials. Extends HedgePosition with
// derivation metadata — still category 'hedge', so all generic
// utils work on it unchanged.

export interface DerivedHedgePosition extends HedgePosition {
  base_formula_code?: string;
  derived_from_base?: boolean;
  allocation_percentage?: number;
  original_quantity?: number; // After allocation %, before price ratio division
}

// ─── Union Type ─────────────────────────────────────────────

export type Position = SupplyPosition | DemandPosition | HedgePosition;

// ─── Type Guards ────────────────────────────────────────────

export function isSupply(p: Position): p is SupplyPosition {
  return p.category === 'supply';
}

export function isDemand(p: Position): p is DemandPosition {
  return p.category === 'demand';
}

export function isHedge(p: Position): p is HedgePosition {
  return p.category === 'hedge';
}
